import { formatDuration, minutesToHm } from '@reditelji/shared';
import { useMemo } from 'react';
import { useCheckinMutations } from '../api/hooks';
import { useSession } from '../api/session';
import type { TimelinePayload } from '../api/types';
import { LATE_LEVELS, LIVE_KEY, liveVals } from '../live/derive';
import { ini } from '../lib/ui';
import { useUi } from '../state/ui';

/**
 * "V živo" side panel: current time + on-site count, the
 * late list ("Zamuja / ni prijave"), the next handovers — done once everyone
 * arriving has checked in — and
 * shifts starting within 45 minutes.
 */
export function LivePanel({ data, now }: { data: TimelinePayload; now: number }) {
  const { selectShift, selectGap, pingNow } = useUi();
  const checkinMut = useCheckinMutations();
  // The organisation watches race day; checking people in and ticking handovers
  // stays with the marshal team (SPEC §1.1L).
  const canAct = useSession().can('live:act');
  const vals = useMemo(() => liveVals(data, now, true), [data, now]);

  return (
    <div className="tl-live">
      <div className="tl-live-head">
        <span className="tl-live-clock">{minutesToHm(now)}</span>
        <span className="tl-live-onsite">{vals.onSite} prostovoljcev na terenu</span>
        <button type="button" className="tl-live-back" onClick={() => pingNow()}>
          Nazaj na zdaj
        </button>
      </div>

      <div className="tl-live-key">
        {LIVE_KEY.map((k) => (
          <span key={k.label} className="tl-live-key-item">
            <span className="tl-live-key-swatch" style={{ background: k.bg, border: k.bd }} />
            {k.label}
          </span>
        ))}
      </div>

      <SectionHead dot="oklch(0.6 0.15 25)" label="Zamuja / ni prijave" count={vals.lateList.length} />
      {vals.lateList.length === 0 ? (
        <div className="tl-live-empty tl-live-empty--ok">
          Vsi, ki bi morali biti na mestu, so prijavljeni.
        </div>
      ) : (
        <div className="tl-live-list">
          {vals.lateList.map((lt) => {
            const c = LATE_LEVELS[lt.level];
            return (
              <div
                key={lt.shift.id}
                className="tl-live-row"
                style={{ background: c.bg, borderColor: c.bd, color: c.fg }}
                onClick={() => selectShift(lt.shift.id)}
              >
                <span className="tl-live-avatar" style={{ color: c.fg }}>
                  {ini(lt.name)}
                </span>
                <div className="tl-live-row-main">
                  <div className="tl-live-row-name">{lt.name}</div>
                  <div className="tl-live-row-sub">
                    {lt.where} · {c.label}
                  </div>
                  <div className="tl-live-row-mono">{lt.since}</div>
                </div>
                {canAct ? (
                  <button
                    type="button"
                    className="tl-live-action"
                    style={{ color: c.fg, borderColor: c.bd }}
                    onClick={(e) => {
                      e.stopPropagation();
                      checkinMut.set.mutate({ shiftId: lt.shift.id, kind: 'in' });
                    }}
                  >
                    Prijava
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <SectionHead
        dot="oklch(0.62 0.09 245)"
        label="Naslednje predaje"
        count={
          vals.handCount > vals.handList.length
            ? `${vals.handList.length} od ${vals.handCount}`
            : vals.handList.length
        }
      />
      {vals.handList.length === 0 ? (
        <div className="tl-live-empty">V naslednjih dveh urah ni predaj.</div>
      ) : (
        <div className="tl-live-list">
          {vals.handList.map((hv) => (
            <div
              key={hv.key}
              className={`tl-live-hand${hv.done ? ' tl-live-hand--done' : hv.risky ? ' tl-live-hand--risky' : ''}`}
            >
              <span
                className={`tl-live-tick${hv.done ? ' tl-live-tick--on' : ''}`}
                title={hv.done ? 'vsi prihajajoči so prijavljeni' : 'opravljeno, ko se prijavijo vsi prihajajoči'}
              >
                {hv.done ? '✓' : ''}
              </span>
              <div className="tl-live-row-main" onClick={() => selectShift(hv.ins[0]!.id)}>
                <div className="tl-live-hand-top">
                  <span className="tl-live-hand-time">{minutesToHm(hv.at)}</span>
                  <span className="tl-live-hand-in">
                    {hv.at <= now ? 'zdaj' : `čez ${formatDuration(hv.at - now)}`}
                  </span>
                  <span className="tl-live-hand-post">{hv.postCode}</span>
                </div>
                <div
                  className="tl-live-hand-pair"
                  style={hv.done ? { textDecoration: 'line-through' } : undefined}
                >
                  {hv.outName} → {hv.inName}
                </div>
                {hv.inExternal && !hv.inOnSite ? (
                  // A team does not check in with us, so "še ni prijavljena"
                  // would be a permanent complaint. The useful thing on the day
                  // is who to phone (SPEC §1.1J).
                  <div className="tl-live-hand-note" style={{ color: 'oklch(0.45 0.014 90)' }}>
                    zunanja ekipa
                    {hv.inLeader ? (
                      <>
                        {' · '}
                        {hv.inLeader.name}{' '}
                        <a className="tl-live-tel" href={`tel:${hv.inLeader.phone}`}>
                          {hv.inLeader.phone}
                        </a>
                      </>
                    ) : null}
                  </div>
                ) : (
                  <div
                    className="tl-live-hand-note"
                    style={{ color: hv.inOnSite ? 'oklch(0.5 0.07 155)' : 'oklch(0.52 0.1 45)' }}
                  >
                    {hv.inOnSite
                      ? 'zamenjava je na mestu'
                      : hv.arrived > 0
                        ? `prijavljena ${hv.arrived} od ${hv.expected}`
                        : 'zamenjava še ni prijavljena'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <SectionHead dot="oklch(0.7 0.09 75)" label="Kmalu začnejo" count={vals.soonList.length} />
      {vals.soonList.length === 0 ? (
        <div className="tl-live-empty">V naslednjih 45 minutah se ne začne nobena izmena.</div>
      ) : (
        <div className="tl-live-list">
          {vals.soonList.map((sv) => (
            <div
              key={sv.shift.id}
              className={`tl-live-row tl-live-row--soon${sv.open ? ' tl-live-row--open' : ''}`}
              onClick={() => selectShift(sv.shift.id)}
            >
              <span className={`tl-live-avatar ${sv.open ? 'tl-live-avatar--open' : 'tl-live-avatar--soon'}`}>
                {sv.open ? '—' : ini(sv.name)}
              </span>
              <div className="tl-live-row-main">
                <div className="tl-live-row-name">{sv.name}</div>
                <div className="tl-live-row-sub">
                  {sv.where}
                  {sv.external ? (
                    <>
                      {' · zunanja ekipa'}
                      {sv.leader ? (
                        <>
                          {' · '}
                          {sv.leader.name}{' '}
                          <a
                            className="tl-live-tel"
                            href={`tel:${sv.leader.phone}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {sv.leader.phone}
                          </a>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </div>
                <div className="tl-live-row-mono">
                  {sv.startsIn <= 0
                    ? `teče od ${minutesToHm(sv.shift.fromMin)}`
                    : `začne ob ${minutesToHm(sv.shift.fromMin)} · čez ${formatDuration(sv.startsIn)}`}
                </div>
              </div>
              {sv.open ? (
                // Nobody to check in: the useful next step is who could go.
                <button
                  type="button"
                  className="tl-live-action tl-live-action--red"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectGap({
                      postId: sv.shift.postId,
                      fromMin: sv.shift.fromMin,
                      toMin: sv.shift.toMin,
                      shiftId: sv.shift.id,
                    });
                  }}
                >
                  Poišči
                </button>
              ) : canAct ? (
                <button
                  type="button"
                  className="tl-live-action tl-live-action--blue"
                  onClick={(e) => {
                    e.stopPropagation();
                    checkinMut.set.mutate({ shiftId: sv.shift.id, kind: 'in' });
                  }}
                >
                  Prijava
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionHead({
  dot,
  label,
  count,
}: {
  dot: string;
  label: string;
  count: number | string;
}) {
  return (
    <div className="tl-live-section">
      <span className="tl-live-section-dot" style={{ background: dot }} />
      <span className="tl-live-section-label">{label}</span>
      <span className="tl-live-section-count">{count}</span>
    </div>
  );
}
